const subscribers = new Set();
let intervalId = null;

const runTick = () => {
  if (document.hidden) {
    return;
  }

  subscribers.forEach((timer) => timer.tick());
};

const subscribe = (timer) => {
  subscribers.add(timer);

  if (!intervalId) {
    intervalId = setInterval(runTick, 1000);
  }
};

const unsubscribe = (timer) => {
  subscribers.delete(timer);

  if (!subscribers.size && intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
};

document.addEventListener('visibilitychange', runTick);

const pad = (value) => String(value).padStart(2, '0');

class PdpUrgencyTimer extends HTMLElement {
  connectedCallback() {
    this.expiresAt = this.getExpirationTime();

    if (!this.expiresAt) {
      return;
    }

    // Liquid can't see a per-visitor session deadline, so a session timer that had already run out before this page load is removed here
    if (this.dataset.timerMode !== 'fixed_date' && this.dataset.expirationBehavior === 'hide' && this.expiresAt <= Date.now()) {
      this.closest('.slovic-pdp-urgency-timer')?.remove();
      return;
    }

    if (this.tick()) {
      subscribe(this);
    }
  }

  disconnectedCallback() {
    unsubscribe(this);
  }

  getExpirationTime() {
    if (this.dataset.timerMode === 'fixed_date' && this.dataset.expiresAt) {
      const normalized = this.dataset.expiresAt.trim().replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
      const fixedDate = Date.parse(normalized);

      return Number.isNaN(fixedDate) ? null : fixedDate;
    }

    const durationMs = (parseInt(this.dataset.durationMinutes, 10) || 30) * 60 * 1000;
    const storageKey = this.dataset.storageKey;

    if (!storageKey) {
      return Date.now() + durationMs;
    }

    try {
      const stored = sessionStorage.getItem(storageKey);

      if (stored) {
        return parseInt(stored, 10);
      }

      const expiresAt = Date.now() + durationMs;
      sessionStorage.setItem(storageKey, expiresAt.toString());
      return expiresAt;
    } catch (error) {
      return Date.now() + durationMs;
    }
  }

  tick() {
    const totalSeconds = Math.max(0, Math.floor((this.expiresAt - Date.now()) / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.updateUnit('hours', hours);
    this.updateUnit('minutes', minutes);
    this.updateUnit('seconds', seconds);
    this.setAttribute('aria-label', `${hours} hours, ${minutes} minutes, ${seconds} seconds remaining`);

    if (totalSeconds > 0) {
      return true;
    }

    unsubscribe(this);

    if (this.dataset.expirationBehavior !== 'hide') {
      this.showExpiredMessage();
    }

    return false;
  }

  showExpiredMessage() {
    const wrapper = this.closest('.slovic-pdp-urgency-timer');

    if (!wrapper) {
      return;
    }

    wrapper.classList.add('is-expired');

    if (this.dataset.expiredMessage && !wrapper.querySelector('.slovic-pdp-urgency-timer__expired')) {
      const expiredMessage = document.createElement('p');
      expiredMessage.className = 'slovic-pdp-urgency-timer__expired';
      expiredMessage.textContent = this.dataset.expiredMessage;
      wrapper.querySelector('.slovic-pdp-urgency-timer__content')?.appendChild(expiredMessage);
    }
  }

  updateUnit(unit, value) {
    const element = this.querySelector(`[data-unit="${unit}"]`);

    if (element) {
      element.textContent = pad(value);
    }
  }
}

if (!customElements.get('pdp-urgency-timer')) {
  customElements.define('pdp-urgency-timer', PdpUrgencyTimer);
}
